import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import getStrategies, {FeedType} from '../../../../utils/getStrategies';

const getStrategiesToolSchema = z.object({
  prompt: z.string().min(1).describe("The user's natural language request to match"),
});

const responseSchema = z.object({
  strategyId: z.number(),
  name: z.string(),
  description: z.string(),
});

export default function getStrategiesTool(store: CLStore) {
  async function execute({
    input: {prompt},
    toolUtils: {agentScratchpad, invokeStandaloneLLMCall},
    callChain,
  }: ToolExecuteParams<
    typeof getStrategiesToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    try {
      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolResult('Campaign not found');
      }

      const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
        agentScratchpad,
        NodeType.PRODUCTS,
      );

      const campaignType = campaign.type;

      const systemPrompt = buildSystemPrompt(FeedType.Product, campaignType);

      const messages = [
        openAISystemMessage(systemPrompt),
        openAIUserMessage(
          `User Prompt: ${prompt} \n
          Return the best matching strategy from the catalogue.`,
        ),
      ];

      const result = await invokeStandaloneLLMCall({
        messages,
        responseSchema,
        callChain,
      });

      // Store available strategies in scratchpad
      const strategies = getStrategies(FeedType.Product, campaignType);

      validatedScratchpad.card.availableStrategies = strategies;

      return toolResult(`The following strategy was selected: ${JSON.stringify(result)}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolResult(`Error matching strategy: ${message}`);
    }
  }

  return tool({
    name: V2CardAgentToolName.GetStrategies,
    displayName: 'Getting the best strategy',
    description: 'Selects the best product selection strategy based on user requirements',
    execute,
    inputSchema: getStrategiesToolSchema,
  });
}

function buildSystemPrompt(feedType: FeedType, campaignType: number) {
  const strategies = getStrategies(feedType, campaignType);

  const catalogue = strategies
    .map((s) => {
      return `ID: ${s.strategyId}
Name: ${s.name}
Description: ${s.description}`;
    })
    .join('\n\n');

  const specificFeedTypeRules = `- When the user's request involves conditioning, prefer the ${ProductSelectionStrategyType.ProductMatching} strategy.
- When the user's request involves specific item selection, prefer the ${ProductSelectionStrategyType.SpecificProducts} strategy.
- When the user's request involves viewed items, prefer the ${ProductSelectionStrategyType.ViewedItems} strategy.`;

  return `You are a precise matcher that selects the single best strategy for a user's request.
You need to analyze each and every strategy. Explain how well it fits the user's intent and choose the one that fits the best.

Strict rules:
- Never use strategies that are not in the strategies catalogue.
- If more than one strategy fits the user's intent, score each strategy and choose the one with the highest score.

${specificFeedTypeRules}

Strategies catalogue:
${catalogue}`;
}
